import React, { ReactNode, useCallback, useRef, useState } from 'react'
import { motion, cubicBezier } from 'framer-motion'
import { Flex } from '@chilipiper/design-system'
import { sanitize, unescapeContent } from '@chilipiper/utils'
import {
  MessageBlock as BlockMessage,
  LiveMessageBlock,
  PiperAiMessageBlock,
} from '../../../chat-data/types'
import { useVisibleForSomeTime } from '../../../hooks/useVisibleForSomeTime'
import { parseMarkdown } from '../../../helpers/markdown'
import { usePageVisibility } from '../../PageVisibilityContext'
import { StyledTextContainer, StyledText } from './styles'

interface MessageBlockProps<T> {
  block: T
  isOwnMessage: boolean
  onMessageVisible?: (event: T) => void
}

export const MessageBlock = <T extends BlockMessage | LiveMessageBlock | PiperAiMessageBlock>({
  block,
  isOwnMessage,
  onMessageVisible,
}: MessageBlockProps<T>) => {
  const elementRef = useRef<HTMLDivElement>(null)
  const [hoveringVideo, setHoveringVideo] = useState(false)

  const handleRead = useCallback(() => {
    onMessageVisible?.(block)
  }, [block, onMessageVisible])
  const isPageVisible = usePageVisibility()

  useVisibleForSomeTime({
    element: elementRef,
    minTime: 100,
    onVisible: handleRead,
    isPageVisible,
  })

  return (
    <Flex
      flexDirection='column'
      gap={2}
      ref={elementRef}
      padding={0}
      maxWidth='268px'
      alignItems={isOwnMessage ? 'end' : 'start'}
      data-test-id='WidgetMessageBlock'
      overflow='hidden'
    >
      {block.parts.map((item, index) => {
        switch (item.type) {
          case 'Text': {
            const html =
              block.type === 'PiperAiMessage'
                ? parseMarkdown(item.text)
                : sanitize(unescapeContent(item.text), ['a', 'p'], [], true)
            return (
              <RevealAnimation key={index} data-test-id='WidgetMessageBlock-text'>
                <StyledTextContainer isOwnMessage={isOwnMessage} p={4} borderRadius={16}>
                  <StyledText
                    textStyle='body-multiline-tight'
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                </StyledTextContainer>
              </RevealAnimation>
            )
          }
          case 'Image':
            return (
              <RevealAnimation key={index} data-test-id='WidgetMessageBlock-image'>
                <Flex
                  as='img'
                  borderRadius={16}
                  width='min(100%, 450px)'
                  maxWidth='100%'
                  maxHeight='400px'
                  src={item.url}
                />
              </RevealAnimation>
            )
          case 'Video':
            return (
              <RevealAnimation key={index}>
                <StyledTextContainer
                  isOwnMessage={isOwnMessage}
                  as={Flex}
                  flexDirection='column'
                  flex='fit-content'
                  textStyle='body-multiline-tight'
                  maxWidth='450px'
                  color='fg/major'
                  fontWeight={500}
                  borderRadius={16}
                  overflow='hidden'
                >
                  <Flex
                    data-test-id='WidgetMessageBlock-video'
                    as='video'
                    controls={hoveringVideo}
                    muted
                    loop
                    autoPlay
                    maxWidth='100%'
                    maxHeight='400px'
                    onMouseEnter={() => setHoveringVideo(true)}
                    onMouseLeave={() => setHoveringVideo(false)}
                  >
                    <source src={item.url} />
                  </Flex>
                  {item.caption && (
                    <StyledText
                      data-test-id='WidgetMessageBlock-videoCaption'
                      p={4}
                      textStyle='body-multiline-tight'
                    >
                      {item.caption}
                    </StyledText>
                  )}
                </StyledTextContainer>
              </RevealAnimation>
            )
          default:
            return null
        }
      })}
    </Flex>
  )
}

const easing = cubicBezier(0.42, 0, 0.58, 1)

const RevealAnimation = ({
  children,
  'data-test-id': dateTestId,
}: {
  children: ReactNode
  'data-test-id'?: string
}) => (
  <motion.div
    animate={{ opacity: 1, y: 0 }}
    initial={{ opacity: 0, y: 10 }}
    transition={{ ease: easing, duration: 0.25 }}
    data-test-id={dateTestId}
    style={{ maxWidth: '100%' }}
  >
    {children}
  </motion.div>
)
